/* Opt-in still/video playback. Native controls remain the visitor's authority.
 * Shared by all campaign assets; no automatic network/cart/theme mutation. */
(function () {
  'use strict';
  var key = '__spmMediaRuntimeV1';
  if (window[key]) { window[key].scan(); return; }
  var selector = '[data-spm-media-autoplay="true"]';
  var states = new Map(), remembered = new WeakMap(), owners = new WeakMap(), pauseSignals = new WeakMap(), suspended = false;
  var internalPauses = new WeakSet();
  var motion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  var observer = null, mutations = null;
  function on(node, type, fn) {
    node.addEventListener(type, fn);
    return function () { node.removeEventListener(type, fn); };
  }
  function visible(state) {
    var root = state.root;
    if (suspended || document.hidden || state.disposed || !root.isConnected ||
        root.closest('[hidden], [aria-hidden="true"], [inert]') || !root.getClientRects().length) return false;
    var style = window.getComputedStyle && window.getComputedStyle(root);
    if (style && (style.visibility === 'hidden' || style.visibility === 'collapse' || style.display === 'none')) return false;
    if (observer) return state.inView;
    var rect = root.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right > 0 && rect.left < (window.innerWidth || document.documentElement.clientWidth);
  }
  function pause(state) {
    if (owners.get(state.video) !== state) return;
    if (state.pending) state.pending.cancelled = true;
    // Browser pause events are queued and can reach a new section owner. Keep
    // credits with the media element, rather than the disposed controller.
    if (!state.video.paused) {
      var video = state.video;
      var signal = pauseSignals.get(video) || {count: 0, listener: null};
      pauseSignals.set(video, signal);
      if (!signal.listener) {
        signal.listener = function (event) {
          if (signal.count) { signal.count--; internalPauses.add(event); }
          if (!signal.count) {
            video.removeEventListener('pause', signal.listener, true);
            signal.listener = null;
          }
        };
        // Capture precedes the owner's native-control listener, including if
        // the section remounts before this queued media event is delivered.
        video.addEventListener('pause', signal.listener, true);
      }
      signal.count++;
      state.video.pause();
    }
  }
  function sync(state) {
    if (!visible(state)) { pause(state); return; }
    if (!state.video.paused || state.pending || state.userPaused || state.failed ||
        (motion && motion.matches) || (window.Shopify && window.Shopify.designMode)) return;
    var request = {cancelled: false};
    state.pending = request;
    state.video.playsInline = true;
    function settled(failed) {
      if (state.pending !== request) return;
      state.pending = null;
      // A section can remount the same media element while an older play
      // promise is still pending. Its old callback no longer owns that video.
      if (owners.get(state.video) !== state) return;
      if (state.disposed) { pause(state); return; }
      if (failed && !request.cancelled) { state.failed = true; pause(state); }
      if (request.cancelled || !visible(state)) {
        pause(state);
        // A cancelled attempt may finish after returning onscreen. Resume only
        // after it settles; never overlap play promises for one video.
        if (request.cancelled) sync(state);
      }
    }
    try {
      var result = state.video.play();
      if (result && typeof result.then === 'function') result.then(function () { settled(false); }, function () { settled(true); });
      else settled(false);
    } catch (error) { settled(true); }
  }
  function syncAll() { states.forEach(sync); }
  function dispose(state) {
    if (state.disposed) return;
    state.disposed = true;
    pause(state);
    remembered.set(state.video, {userPaused: state.userPaused, failed: state.failed});
    state.cleanups.forEach(function (cleanup) { cleanup(); });
    if (observer) observer.unobserve(state.root);
    states.delete(state.root);
  }
  function init(root) {
    if (states.has(root)) return;
    var video = root.querySelector('video');
    if (!video) return;
    var prior = remembered.get(video) || {};
    var state = {root: root, video: video, inView: false, disposed: false, pending: null,
      userPaused: !!prior.userPaused, failed: !!prior.failed, cleanups: []};
    states.set(root, state);
    owners.set(video, state);
    // Initial automatic playback is muted. Later visibility resumes retain
    // the visitor's volume choice, including after bfcache/section remounts.
    if (!remembered.has(video)) video.muted = true;
    video.autoplay = false;
    video.removeAttribute('autoplay');
    video.playsInline = true;
    state.cleanups.push(on(video, 'play', function () {
      if (!state.pending) { state.userPaused = false; state.failed = false; }
      if (!visible(state)) pause(state);
    }));
    state.cleanups.push(on(video, 'pause', function (event) {
      if (internalPauses.has(event)) return;
      state.userPaused = true;
      if (state.pending) state.pending.cancelled = true;
    }));
    state.cleanups.push(on(video, 'ended', function () { state.userPaused = true; }));
    state.cleanups.push(on(video, 'error', function () { state.failed = true; pause(state); }));
    if (observer) observer.observe(root);
    sync(state);
  }
  function scan() {
    if (suspended) return;
    states.forEach(function (state) {
      if (!state.root.isConnected || !state.root.matches(selector) || state.root.querySelector('video') !== state.video) dispose(state);
    });
    document.querySelectorAll(selector).forEach(init);
    syncAll();
  }
  function start() {
    if (suspended) return;
    if (!observer && window.IntersectionObserver) {
      observer = new window.IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var state = states.get(entry.target);
          if (state) { state.inView = entry.isIntersecting; sync(state); }
        });
      }, {threshold: 0});
    }
    if (!mutations && window.MutationObserver) {
      mutations = new window.MutationObserver(scan);
      mutations.observe(document.documentElement, {subtree: true, childList: true, attributes: true,
        attributeFilter: ['hidden', 'aria-hidden', 'inert', 'style', 'class', 'open', 'data-spm-media-autoplay']});
    }
    scan();
  }
  window[key] = {scan: scan};
  on(document, 'visibilitychange', syncAll);
  on(document, 'shopify:section:load', scan);
  on(document, 'shopify:section:unload', function (event) {
    states.forEach(function (state) { if (event.target && event.target.contains(state.root)) dispose(state); });
  });
  on(window, 'scroll', syncAll);
  on(window, 'resize', syncAll);
  if (motion) {
    var changed = function () { if (motion.matches) states.forEach(pause); syncAll(); };
    if (motion.addEventListener) on(motion, 'change', changed);
    else if (motion.addListener) motion.addListener(changed);
  }
  on(window, 'pagehide', function () {
    suspended = true;
    states.forEach(dispose);
    if (observer) { observer.disconnect(); observer = null; }
    if (mutations) { mutations.disconnect(); mutations = null; }
  });
  on(window, 'pageshow', function () { suspended = false; start(); });
  if (document.readyState === 'loading') {
    var offReady = on(document, 'DOMContentLoaded', function () { offReady(); start(); });
  } else start();
})();
